/**
 * @prettier
 */

import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import _clone from 'lodash/clone';
import _merge from 'lodash/merge';
import _toPairs from 'lodash/toPairs';
import fromPairs from 'lodash/fromPairs';
import { getUserIsGuest, getUserContext } from 'selectors/user/userSelectors';
import { getIsAdminView } from 'cards/selectors/cardCatalogSelector';
import { getUserContext as getUserContextAction } from 'actions/UserActions';
import { getCart as getCartAction } from 'actions/CartActions';
import MinimalLoader from 'components/common/ui/MinimalLoader';
import FeatureFlags from '../../../../../util/featureFlags';
import { getLocationData } from '../util/api';
import {
  useCardSuiteDetails,
  useCardSuiteDetailsSet,
  usePreviewTemplate,
  useDefaultTemplateSet,
  useSpecialTypeSampleView,
  useSingleSampleLimitExhausted,
} from '../util/hooks';
import {
  extractUserWeddingDetails,
  templateHasCustomImage,
  lookupSelectedVariation,
  lookupDefaultVariation,
  findVariation,
  getColors,
  getIsSampleCustomizable,
} from '../util/helpers';

import { trackEvent } from '../../../../../util/trackingHelper';
import generateUUID from '../../../../../lib/uuid';
import CustomizeWarning from '../CustomizeWarning/CustomizeWarning';
import CartError from '../CartError/CartError';
import FlowOverlay from '../FlowOverlay/FlowOverlay';
import ExitOverlay from '../ExitOverlay/ExitOverlay';
import SampleLimitView from '../SampleLimitView/SampleLimitView';
import MultiSample from '../MultiSample/MultiSample';

import { STEPS } from '../constants/steps';
import { STEP_DATA } from '../constants/stepData';

import styles from './styles.module.less';

const getBaseForm = () => ({
  customizations: {
    color: null,
    colorSet: {},
    variation: null,
    variationSet: {},
    uploadcareFile: null,
    uploadcareFileSet: {},
    photo: { file: null, valid: false, size: null },
    photoSet: {},
    pixelCrop: null,
    pixelCropSet: {},
  },
  weddingDetails: {
    fullName: null,
    partnerFullName: null,
    date: null,
    stillDeciding: false,
  },
  shipping: {
    address: null,
    apt: null,
    city: null,
    state: null,
    zipCode: null,
  },
  account: {
    email: null,
    password: null,
  },
});

const FETCH_STATE = {
  INITIAL: 'INITIAL',
  LOADING: 'LOADING',
  LOADED: 'LOADED',
};

export const SingleSampleFlowBase = props => {
  const {
    isMulti,
    entryPoint,
    card,
    cardSuiteUUID,
    cardSuiteUUIDList,
    overrideActiveVariationUUID,
    initialColor,
    initialColorSet,
    initialForm,
    initialFlowUUID,
    initialStepKey,
    initialMultiPreviewState,
    isGuest,
    userContext,
    cart,
    fetchCart,
    showCustomizeBanner,
    fetchUserContext,
    handleOpenModal,
    initialCloseCallback,
    closeModal,
    setModalOptions,
    customizeHandler,
    isAdminView,
    isMagnet,
    nonEditableSingleSampleSku,
    showExitOverlay,
    setShowExitOverlay,
  } = props;

  const [generatedUuid] = useState(generateUUID());
  const flowUUID = initialFlowUUID || generatedUuid;

  const [loading, setLoading] = useState(true);
  const [form, setFormState] = useState(initialForm || getBaseForm());
  const [stepKey, setStepKey] = useState(initialStepKey);
  const [trackingParams, setTrackingParams] = useState(null);
  const [errors, setErrors] = useState({});
  const [showCartError, setShowCartError] = useState(null);
  const [breadcrumbSteps, setBreadcrumbSteps] = useState([]);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [wasGuestOnLoad, setWasGuestOnLoad] = useState();
  const [fetchedUserContext, setFetchedUserContext] = useState(false);
  const [fetchedCart, setFetchedCart] = useState(FETCH_STATE.INITIAL);
  const [fetchedSampleLimit, setFetchedSampleLimit] = useState(FETCH_STATE.INITIAL);
  const [fetchedLocationData, setFetchedLocationData] = useState(FETCH_STATE.INITIAL);

  const [multiPreviewState, setMultiPreviewStateBase] = useState(
    initialMultiPreviewState || {
      initialized: false,
      activePreview: card && card.suiteUUID,
      cropEnabled: false,
    }
  );

  const setMultiPreviewState = useCallback(
    newState => {
      if (form.customizations.photoSet[multiPreviewState.activePreview]) {
        setMultiPreviewStateBase(newState);
      } else {
        setMultiPreviewStateBase({ ...newState, cropEnabled: false });
      }
    },
    [form, multiPreviewState, setMultiPreviewStateBase]
  );

  let cardSuiteDetails = useCardSuiteDetails(cardSuiteUUID, isAdminView, isMulti);
  const cardSuiteDetailsSet = useCardSuiteDetailsSet(cardSuiteUUIDList, isAdminView, isMulti);
  if (isMulti) {
    cardSuiteDetails = cardSuiteDetailsSet && cardSuiteDetailsSet[multiPreviewState.activePreview];
  }

  let sampleNotCustomizable = !getIsSampleCustomizable(cardSuiteDetails, isMagnet);
  if (isMulti) {
    sampleNotCustomizable = !getIsSampleCustomizable(
      cardSuiteDetailsSet[multiPreviewState.activePreview]
    );
  }

  const template = usePreviewTemplate(
    cardSuiteDetails,
    form,
    sampleNotCustomizable,
    isMagnet,
    isMulti,
    cardSuiteDetailsSet,
    multiPreviewState.activePreview
  );

  const defaultTemplateSet = useDefaultTemplateSet(
    cardSuiteUUIDList,
    cardSuiteDetailsSet,
    isMulti,
    multiPreviewState,
    setMultiPreviewState
  );

  let sampleSku = nonEditableSingleSampleSku;
  if (isMulti) {
    sampleSku =
      cardSuiteDetailsSet &&
      cardSuiteDetailsSet[multiPreviewState.activePreview] &&
      cardSuiteDetailsSet[multiPreviewState.activePreview].single_sample_sku;
  }

  const skuData = useSpecialTypeSampleView(sampleSku);
  const [sampleLimitExhausted, fetchSampleLimitExhausted] = useSingleSampleLimitExhausted(
    cardSuiteDetails && cardSuiteDetails.type
  );

  // Update form values with object patches e.g. updateForm({ shipping: { city: null, zipCode: '12345' } })
  const updateForm = useCallback(patch => setFormState(_clone(_merge(form, patch))), [
    form,
    setFormState,
  ]);

  useEffect(() => {
    if (!loading && isGuest) {
      setWasGuestOnLoad(true);
    }
  }, [loading, isGuest]);

  useEffect(() => {
    if (!fetchedUserContext) {
      fetchUserContext().then(() => {
        setFetchedUserContext(true);
      });
    }

    if (fetchedCart === FETCH_STATE.INITIAL) {
      setFetchedCart(FETCH_STATE.LOADING);
      fetchCart().then(() => setFetchedCart(FETCH_STATE.LOADED));
    }
  }, [fetchedCart, fetchedUserContext, fetchCart, fetchUserContext]);

  useEffect(() => {
    if (fetchedSampleLimit === FETCH_STATE.INITIAL && cardSuiteDetails && cardSuiteDetails.type) {
      setFetchedSampleLimit(FETCH_STATE.LOADING);
      fetchSampleLimitExhausted().then(() => setFetchedSampleLimit(FETCH_STATE.LOADED));
    }
  }, [cardSuiteDetails, fetchedSampleLimit, fetchSampleLimitExhausted]);

  useEffect(() => {
    if (fetchedCart === FETCH_STATE.LOADED && cart && !cart.busy) {
      const showError = cart.size > 0;

      setShowCartError(showError);

      if (showError) {
        setModalOptions({ preventClose: false, hideClose: false });
      }
    }
  }, [fetchedCart, cart, setModalOptions]);

  useEffect(() => {
    if (
      loading &&
      fetchedUserContext &&
      stepKey &&
      template &&
      defaultTemplateSet &&
      cardSuiteDetails &&
      fetchedSampleLimit === FETCH_STATE.LOADED &&
      multiPreviewState.initialized
    ) {
      setLoading(false);
    }
  }, [
    loading,
    fetchedUserContext,
    showCartError,
    stepKey,
    template,
    cardSuiteDetails,
    fetchedSampleLimit,
    defaultTemplateSet,
    multiPreviewState.initialized,
  ]);

  useEffect(() => {
    if (loading) {
      return;
    }

    // Send "Flow Started" event when loading is complete
    const params = {
      entry_point: entryPoint,
      flow_run_id: flowUUID,
      business_unit: 'INVITATIONS',
      // TODO: Use multi sample tracking params
      product_id: cardSuiteDetails.uuid,
      sku_id: card && card.uuid,
      category: cardSuiteDetails.type,
      name: card && card.name,
      brand: cardSuiteDetails.name,
    };
    setTrackingParams(params);
    trackEvent('Flow Started', params);
  }, [loading, cardSuiteDetails, card, entryPoint, flowUUID]);

  // Send "Flow Step Viewed" event every time the user changes steps.
  useEffect(() => {
    if (stepKey && trackingParams) {
      // TODO: only send if step is trackable
      trackEvent('Flow Step Viewed', {
        ...trackingParams,
        step_name: stepKey,
      });
    }
  }, [stepKey, trackingParams]);

  // Prefill the form with existing wedding details
  useEffect(() => {
    const userWeddingDetails = extractUserWeddingDetails(userContext);

    if (userWeddingDetails) {
      const {
        owner_first_name: firstName = '',
        owner_last_name: lastName = '',
        partner_first_name: partnerFirstName = '',
        partner_last_name: partnerLastName = '',
        wedding_date: date,
      } = userWeddingDetails;

      const displayedFullName = firstName || lastName ? `${firstName} ${lastName}`.trim() : null;

      const displayedPartnerFullName =
        partnerFirstName || partnerLastName
          ? `${partnerFirstName} ${partnerLastName}`.trim()
          : null;

      updateForm({
        weddingDetails: {
          fullName: displayedFullName,
          partnerFullName: displayedPartnerFullName,
          date,
        },
      });
    }
  }, [userContext]);

  // Prefill shipping information
  useEffect(() => {
    if (fetchedLocationData === FETCH_STATE.INITIAL) {
      setFetchedLocationData(FETCH_STATE.LOADING);

      getLocationData(userContext).then(locationData => {
        setFetchedLocationData(FETCH_STATE.LOADED);

        if (locationData && locationData.shippingAddress) {
          const shipping = locationData.shippingAddress;

          updateForm({
            shipping: {
              address: shipping.street_address_1,
              apt: shipping.street_address_2,
              city: shipping.city,
              state: shipping.state_province_region,
              zipCode: shipping.postal_code,
            },
          });
        }
      });
    }
  }, [userContext, fetchedLocationData]);

  // Select the first available color by default
  useEffect(() => {
    if (!initialForm && cardSuiteDetails && !form.customizations.color) {
      let color;

      const colors = getColors(cardSuiteDetails);

      if (cardSuiteDetails.letterpress || !initialColor) {
        const defaultColor =
          cardSuiteDetails.mediums &&
          cardSuiteDetails.mediums.paper &&
          cardSuiteDetails.mediums.paper.default_variation_options &&
          cardSuiteDetails.mediums.paper.default_variation_options.color;

        color = colors.find(c => c.value === defaultColor);
      } else {
        color = colors.find(c => c.value === initialColor);
      }

      const defaultVariation = lookupDefaultVariation(cardSuiteDetails, isMagnet);
      const colorVariation = lookupSelectedVariation(
        {
          ...defaultVariation.option_values,
          color: color.value,
        },
        cardSuiteDetails,
        isMagnet
      );
      const overridedUUID = overrideActiveVariationUUID || colorVariation.uuid;
      const variation = findVariation(overridedUUID, cardSuiteDetails, isMagnet);

      updateForm({ customizations: { variation, color: color.value || card.colors[0].value } });
    }
  }, [
    overrideActiveVariationUUID,
    initialForm,
    form.customizations.color,
    cardSuiteDetails,
    card,
    initialColor,
    isMagnet,
  ]);

  useEffect(() => {
    if (!initialForm && isMulti && Object.keys(cardSuiteDetailsSet).length > 0 && initialColorSet) {
      const colorSet = { ...initialColorSet };

      const variationSet = fromPairs(
        _toPairs(cardSuiteDetailsSet).map(([key, suiteDetails]) => {
          const defaultVariation = lookupDefaultVariation(suiteDetails, isMagnet);
          const colorVariation = lookupSelectedVariation(
            {
              ...defaultVariation.option_values,
              color: colorSet[key],
            },
            suiteDetails,
            isMagnet
          );

          return [key, colorVariation];
        })
      );

      updateForm({ customizations: { variationSet, colorSet } });
    }
  }, [initialForm, isMulti, cardSuiteDetailsSet, initialColorSet, isMagnet]);

  const enablePhotoUpload =
    cardSuiteDetails &&
    template &&
    templateHasCustomImage(template) &&
    !cardSuiteDetails.foil &&
    !isMagnet;

  let multiHasCustomizableCard = false;
  let multiHasCustomImage = false;
  if (isMulti && cardSuiteDetailsSet && template && defaultTemplateSet) {
    multiHasCustomizableCard = !!Object.keys(defaultTemplateSet).find(k =>
      getIsSampleCustomizable(cardSuiteDetailsSet[k])
    );
    multiHasCustomImage = !!Object.keys(defaultTemplateSet).find(
      k =>
        templateHasCustomImage(defaultTemplateSet[k]) &&
        getIsSampleCustomizable(cardSuiteDetailsSet[k])
    );
  }

  // As soon as breadcrumbSteps is set, select the first step.
  useEffect(() => {
    if (!stepKey && breadcrumbSteps.length > 0) {
      setStepKey(breadcrumbSteps[0]);
    }
  }, [breadcrumbSteps, stepKey]);

  // As soon as we have template data, we can determine the breadcrumb steps
  useEffect(() => {
    if (!template || (isMulti && !defaultTemplateSet) || !fetchedUserContext) return;

    const possibleAccountDetailsStep = isGuest ? [STEPS.ACCOUNT_DETAILS_FORM] : [];

    if (isMulti) {
      const possibleFileUploadSteps = multiHasCustomImage
        ? [STEPS.FILE_UPLOAD, STEPS.MULTI_UPLOAD_HELP]
        : [];

      const possibleCustomizationStep = multiHasCustomizableCard ? [STEPS.CUSTOMIZATION] : [];

      setBreadcrumbSteps([
        ...possibleFileUploadSteps,
        ...possibleCustomizationStep,
        STEPS.WEDDING_DETAILS_FORM,
        STEPS.SHIPPING_DETAILS_FORM,
        ...possibleAccountDetailsStep,
      ]);
      return;
    }

    const possibleFileUploadStep =
      templateHasCustomImage(template) && !cardSuiteDetails.foil && !isMagnet
        ? [STEPS.FILE_UPLOAD]
        : [];

    const possibleCustomizeStep =
      FeatureFlags.get('invitesSingleSampleCustomizeStep') && !sampleNotCustomizable
        ? [STEPS.CUSTOMIZATION]
        : [];

    setBreadcrumbSteps([
      ...possibleFileUploadStep,
      ...possibleCustomizeStep,
      STEPS.WEDDING_DETAILS_FORM,
      STEPS.SHIPPING_DETAILS_FORM,
      ...possibleAccountDetailsStep,
    ]);
  }, [
    template,
    fetchedUserContext,
    isGuest,
    isMulti,
    defaultTemplateSet,
    cardSuiteDetails,
    isMagnet,
    multiHasCustomImage,
    multiHasCustomizableCard,
    sampleNotCustomizable,
  ]);

  const handleFlowError = useCallback((submissionErrors, redirectKey) => {
    setErrors(submissionErrors);
    setStepKey(redirectKey);
  }, []);

  const step = STEP_DATA[stepKey];

  const openModal = useCallback(
    overrideProps =>
      handleOpenModal({
        ...props,
        ...overrideProps,
        initialFlowUUID: flowUUID,
        initialStepKey: step.key,
        initialModalContext: { showExitOverlay },
        initialMultiPreviewState: { ...multiPreviewState },
      }),
    [flowUUID, multiPreviewState, props, showExitOverlay, step, handleOpenModal]
  );

  useEffect(() => {
    if (isMulti && multiPreviewState.cropEnabled) {
      setModalOptions({ hideClose: true, closeCallback: () => {} });
      return;
    }

    setModalOptions({ hideClose: false, closeCallback: initialCloseCallback });
  }, [isMulti, multiPreviewState.cropEnabled, initialCloseCallback, setModalOptions]);

  const hideOverlay = () => {
    setShowExitOverlay(false);
    setModalOptions({ preventClose: true, hideClose: false });
  };

  // Render
  if (loading) {
    return <MinimalLoader />;
  }

  if (showCartError) {
    return <CartError closeModal={closeModal} />;
  }

  const cardProps = {
    cardType: cardSuiteDetails.type,
    colors: getColors(cardSuiteDetails),
    isLetterpress: cardSuiteDetails.letterpress,
    isStampedFoil: cardSuiteDetails.foil,
    isMagnet,
  };

  const stepProps = {
    ...props,
    step,
    form,
    errors,
    trackingParams,
    breadcrumbSteps,
    cardSuiteDetails,
    cardSuiteDetailsSet,
    ...cardProps,
    enablePhotoUpload,
    template,
    defaultTemplateSet,
    updateForm,
    handleFlowError,
    completedOrder,
    wasGuestOnLoad,
    setCompletedOrder,
    goToStep: setStepKey,
    fetchUserContext,
    sampleLimitExhausted,
    fetchSampleLimitExhausted,
    sampleNotCustomizable,
    skuData,
    openModal,
    multiPreviewState,
    setMultiPreviewState,
    multiHasCustomizableCard,
    multiHasCustomImage,
  };

  const StepComponent = step.component;

  const showBanner = showCustomizeBanner && breadcrumbSteps.indexOf(step.key) === 0;

  if (
    FeatureFlags.get('invitesSingleSampleLimitScreen') &&
    sampleLimitExhausted &&
    !completedOrder
  ) {
    const text = (
      <Fragment>
        Looks like you’ve ordered the max number of samples. Love a design you tried? Place a full
        order anytime (and get a printed proof before you buy!).
      </Fragment>
    );

    return <SampleLimitView text={text} {...stepProps} />;
  }

  if (isMulti && multiPreviewState.cropEnabled) {
    return <MultiSample.Crop {...stepProps} />;
  }

  return (
    <FlowOverlay
      hideOverlay={hideOverlay}
      hideBack={!isGuest}
      overlayElement={showExitOverlay && <ExitOverlay {...stepProps} />}
    >
      <div className={styles.stepElementBox}>
        {showBanner && <CustomizeWarning onCustomize={customizeHandler} closeModal={closeModal} />}
        <StepComponent {...stepProps} />
      </div>
    </FlowOverlay>
  );
};

const mapStateToProps = state => ({
  isGuest: getUserIsGuest(state),
  userContext: getUserContext(state),
  isAdminView: getIsAdminView(state),
  cart: state.cart,
});

const mapDispatchToProps = {
  fetchUserContext: getUserContextAction,
  fetchCart: getCartAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(SingleSampleFlowBase);
